import React, { useState } from "react";
import { FileText, Lock, Users, X } from "lucide-react";
import { useDebounced } from "../hooks/useApi";
import { usePagedList } from "../hooks/usePagedList";
import { downloadPdf } from "./FileUpload";
import { toLocalISODate } from "../lib/dates";
import { MinutesFilters } from "./MinutesFilters";
import { LoadMore } from "./LoadMore";

const escapeRx = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

function Highlight({ text, q }) {
  if (!q || !text) return <>{text}</>;
  const parts = text.split(new RegExp(`(${escapeRx(q)})`, "ig"));
  let offset = 0;
  return <>{parts.map((p) => {
    const key = `${offset}-${p.length}`; offset += p.length;
    return p.toLowerCase() === q.toLowerCase() ? <mark key={key} className="rounded bg-gold/40 px-0.5 text-crimson">{p}</mark> : <React.Fragment key={key}>{p}</React.Fragment>;
  })}</>;
}

function VisibilityPill({ v }) {
  const officers = v === "officers";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${officers ? "bg-crimson/10 text-crimson" : "bg-palm/15 text-palm"}`}>
      {officers ? <><Lock size={10} /> Officers</> : <><Users size={10} /> Members</>}
    </span>
  );
}

function MinuteBody({ m, q, open }) {
  const text = open || !m.snippet || m.match_in_file ? m.body : m.snippet;
  return (
    <>
      {text && <p className={`mt-2 whitespace-pre-wrap text-sm ${open ? "" : "line-clamp-4"}`} data-testid={`minutes-body-${m.id}`}><Highlight text={text} q={q} /></p>}
      {m.match_in_file && <p className="mt-2 rounded-lg bg-gold/10 p-2 text-xs italic" data-testid={`minutes-file-snippet-${m.id}`}><FileText size={12} className="mr-1 inline" />Found in file: <Highlight text={m.snippet} q={q} /></p>}
    </>
  );
}

function MinuteCard({ m, q }) {
  const [open, setOpen] = useState(false);
  return (
    <div id={`minutes-${m.id}`} className="glass rounded-2xl p-4 scroll-mt-24" data-testid={`minutes-${m.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-semibold text-crimson"><Highlight text={m.title} q={q} /></p>
        <VisibilityPill v={m.visibility} />
      </div>
      <p className="text-xs text-muted-foreground"><Highlight text={m.event_title} q={q} />{m.event_date ? ` · ${m.event_date}` : ""} · {m.author} · {toLocalISODate(m.created_at)}{m.adopted ? " · adopted" : ""}</p>
      <MinuteBody m={m} q={q} open={open} />
      <div className="mt-2 flex flex-wrap gap-4">
        {m.body?.length > 240 && <button onClick={() => setOpen((v) => !v)} className="min-h-[36px] text-sm font-semibold text-crimson hover:underline" data-testid={`minutes-toggle-${m.id}`}>{open ? "Show less" : "Read full minutes"}</button>}
        {m.file_url && <button onClick={() => downloadPdf(m.file_url.replace(/^\/api/, ""), `${m.title}.pdf`)} className="inline-flex min-h-[36px] items-center gap-1 text-sm font-semibold text-palm hover:underline" data-testid={`minutes-file-${m.id}`}><FileText size={14} /> Open minutes file</button>}
      </div>
    </div>
  );
}

const EMPTY = { q: "", eventId: "all", year: "all", adopted: false };

function buildPath(f, q) {
  const p = new URLSearchParams({ limit: "20" });
  if (q) p.set("q", q);
  if (f.eventId !== "all") p.set("event_id", f.eventId);
  if (f.year !== "all") p.set("year", f.year);
  if (f.adopted) p.set("adopted", "true");
  return `/meetings/minutes?${p.toString()}`;
}

export function MinutesList({ title = "Meeting minutes" }) {
  const [f, setF] = useState(EMPTY);
  const q = useDebounced(f.q.trim(), 300);
  const { data, items, total, busy, loadMore, hasMore } = usePagedList(buildPath(f, q));
  const active = Boolean(q || f.eventId !== "all" || f.year !== "all" || f.adopted);
  return (
    <section className="mt-10" data-testid="minutes-list">
      <h3 className="mb-3 flex items-center gap-2 font-serif-display text-2xl font-bold text-crimson"><FileText size={20} className="text-gold" /> {title}</h3>
      <MinutesFilters f={f} setF={setF} />
      {data && (
        <p className="mb-3 flex items-center gap-3 text-xs text-muted-foreground" data-testid="minutes-result-count">
          Showing {items.length} of {total} {total === 1 ? "result" : "results"}
          {active && <button onClick={() => setF(EMPTY)} className="inline-flex items-center gap-1 font-semibold text-crimson hover:underline" data-testid="minutes-clear-filters"><X size={12} /> Clear filters</button>}
        </p>
      )}
      {data && total === 0 && <p className="text-sm text-muted-foreground" data-testid="minutes-empty">{active ? "No minutes match your search." : "No minutes have been published yet."}</p>}
      <div className="grid gap-3 md:grid-cols-2">{items.map((m) => <MinuteCard key={m.id} m={m} q={q} />)}</div>
      {hasMore && <LoadMore remaining={total - items.length} busy={busy} onClick={loadMore} testId="minutes-load-more" />}
    </section>
  );
}
